import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { parceirosApi, servicosApi } from '../lib/api';
import { formatCurrency, formatDate, formatOrdemNumero, getStatusColor } from '../lib/utils';
import { ParceiroFormDialog } from './Parceiros';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ChevronLeft, Pencil, Plus, Loader2, KeyRound, TrendingUp, FileUp, Trash2, Settings2 } from 'lucide-react';

const DIAS_SEMANA = [
  { v: 'segunda', t: 'Segunda-feira' }, { v: 'terca', t: 'Terça-feira' }, { v: 'quarta', t: 'Quarta-feira' },
  { v: 'quinta', t: 'Quinta-feira' }, { v: 'sexta', t: 'Sexta-feira' }, { v: 'sabado', t: 'Sábado' }, { v: 'domingo', t: 'Domingo' },
];
const CICLO_LABEL = { por_servico: 'Por OS', semanal: 'Semanal', quinzenal: 'Quinzenal', mensal: 'Mensal' };

const STATUS_COR = {
  Ativo: 'bg-green-100 text-green-700', Inativo: 'bg-gray-100 text-gray-600',
  Bloqueado: 'bg-red-100 text-red-700', Pendente: 'bg-amber-100 text-amber-700',
};
const APROVACAO_LABEL = {
  aguardando_valor: 'Aguardando valor', aguardando_aprovacao: 'Aguardando aprovação do parceiro',
  aprovado_credito: 'Aprovado (crédito)', pagamento_antecipado: 'Pagamento antecipado',
};

export default function ParceiroPerfilAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [p, setP] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [imoveis, setImoveis] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [faturas, setFaturas] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [auditoria, setAuditoria] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showUsuario, setShowUsuario] = useState(false);
  const [showLimite, setShowLimite] = useState(false);
  const [showFatura, setShowFatura] = useState(false);
  const [showSolicitacao, setShowSolicitacao] = useState(false);
  const [showFatConfig, setShowFatConfig] = useState(false);
  const [fatConfig, setFatConfig] = useState({ ciclo: 'semanal', prazo_dias: 7, dia_fechamento: '', desconto_tipo: 'sem', desconto_pct: 0, condicoes: '' });
  const [busy, setBusy] = useState(false);

  const [novoUsuario, setNovoUsuario] = useState({ nome: '', cargo: '', email: '', whatsapp: '', permissao: 'solicitante', senha: '' });
  const [editUsuario, setEditUsuario] = useState(null);
  const [limiteForm, setLimiteForm] = useState({ novo_limite: '', motivo: '', observacao: '' });
  const [faturaForm, setFaturaForm] = useState({ periodo_inicio: '', periodo_fim: '' });

  const load = useCallback(() => {
    parceirosApi.get(id).then((r) => setP(r.data)).catch(() => navigate('/sistema/parceiros'));
    parceirosApi.listUsuarios(id).then((r) => setUsuarios(r.data));
    parceirosApi.listImoveis(id).then((r) => setImoveis(r.data));
    parceirosApi.listOrdens(id).then((r) => setOrdens(r.data));
    parceirosApi.faturasDoParceiro(id).then((r) => setFaturas(r.data));
    parceirosApi.historicoLimite(id).then((r) => setHistorico(r.data));
    parceirosApi.listDocumentos(id).then((r) => setDocumentos(r.data));
    parceirosApi.auditoria(id).then((r) => setAuditoria(r.data));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const criarUsuario = async () => {
    setBusy(true);
    try {
      await parceirosApi.createUsuario(id, novoUsuario);
      toast.success('Usuário criado com a senha definida');
      setShowUsuario(false);
      setNovoUsuario({ nome: '', cargo: '', email: '', whatsapp: '', permissao: 'solicitante', senha: '' });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao criar usuário');
    } finally { setBusy(false); }
  };

  const salvarEdicaoUsuario = async () => {
    setBusy(true);
    try {
      const payload = { nome: editUsuario.nome, cargo: editUsuario.cargo, email: editUsuario.email, whatsapp: editUsuario.whatsapp, permissao: editUsuario.permissao };
      if (editUsuario.nova_senha) payload.senha = editUsuario.nova_senha;
      await parceirosApi.updateUsuario(editUsuario.id, payload);
      toast.success(editUsuario.nova_senha ? 'Login e senha atualizados' : 'Usuário atualizado');
      setEditUsuario(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao atualizar usuário');
    } finally { setBusy(false); }
  };

  const toggleUsuario = async (u) => {
    await parceirosApi.updateUsuario(u.id, { ativo: !u.ativo });
    toast.success(u.ativo ? 'Usuário bloqueado' : 'Usuário reativado');
    load();
  };

  const alterarLimite = async () => {
    setBusy(true);
    try {
      await parceirosApi.alterarLimite(id, { novo_limite: parseFloat(limiteForm.novo_limite), motivo: limiteForm.motivo, observacao: limiteForm.observacao });
      toast.success('Limite atualizado');
      setShowLimite(false);
      setLimiteForm({ novo_limite: '', motivo: '', observacao: '' });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao alterar limite');
    } finally { setBusy(false); }
  };

  const salvarFatConfig = async () => {
    setBusy(true);
    try {
      await parceirosApi.faturamentoConfig(id, { ...fatConfig, prazo_dias: parseInt(fatConfig.prazo_dias || 0, 10), desconto_pct: parseFloat(fatConfig.desconto_pct || 0) });
      toast.success('Configuração de faturamento salva');
      setShowFatConfig(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao salvar configuração');
    } finally { setBusy(false); }
  };

  const gerarFatura = async () => {
    setBusy(true);
    try {
      const r = await parceirosApi.gerarFatura(id, faturaForm);
      toast.success(`Fatura ${r.data.codigo} gerada: ${formatCurrency(r.data.total)}`);
      setShowFatura(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao gerar fatura');
    } finally { setBusy(false); }
  };

  const marcarPaga = async (f) => {
    await parceirosApi.atualizarFaturaStatus(f.id, { status: 'Paga' });
    toast.success('Fatura paga — crédito liberado');
    load();
  };

  const uploadDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      await parceirosApi.uploadDocumentos(id, files);
      toast.success('Documento(s) anexado(s)');
      load();
    } catch { toast.error('Falha no upload'); }
    e.target.value = '';
  };

  if (!p) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={28} /></div>;

  const pct = p.limite_credito > 0 ? Math.min(100, (p.credito_utilizado / p.limite_credito) * 100) : 0;
  const ind = p.indicadores || {};

  return (
    <div className="space-y-4" data-testid="perfil-parceiro-admin">
      <Link to="/sistema/parceiros" className="flex items-center gap-1 text-sm text-gray-500"><ChevronLeft size={16} /> Parceiros</Link>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#3D4F5F] flex items-center gap-2">
            {p.nome}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COR[p.status]}`}>{p.status}</span>
          </h1>
          <div className="text-sm text-gray-500">{p.codigo} · {p.tipo} {p.responsavel && `· ${p.responsavel}`} {p.whatsapp && `· ${p.whatsapp}`}</div>
          {ind.inadimplente && <div className="text-xs font-semibold text-red-600 mt-1">⚠ PAGAMENTO EM ATRASO</div>}
          {p.sugestao_limite && <div className="text-xs font-medium text-[#2B7A3D] mt-1">💡 Sugestão: parceiro elegível para revisão de limite.</div>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="btn-editar-parceiro"><Pencil size={15} className="mr-1" /> Editar</Button>
          <Button className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => setShowSolicitacao(true)} data-testid="btn-nova-solicitacao-admin"><Plus size={15} className="mr-1" /> Nova solicitação</Button>
        </div>
      </div>

      {/* Limite resumo */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h2 className="text-sm font-semibold text-[#3D4F5F]">Limite de crédito</h2>
          <Button size="sm" variant="outline" onClick={() => { setLimiteForm({ novo_limite: p.limite_credito, motivo: '', observacao: '' }); setShowLimite(true); }} data-testid="btn-alterar-limite">
            <TrendingUp size={14} className="mr-1" /> Alterar limite
          </Button>
        </div>
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
          <div className={`h-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-[#2B7A3D]'}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-sm" data-testid="limite-admin">
          <div><div className="text-xs text-gray-500">Limite</div><b>{formatCurrency(p.limite_credito)}</b></div>
          <div><div className="text-xs text-gray-500">Utilizado</div><b className="text-amber-600">{formatCurrency(p.credito_utilizado)}</b></div>
          <div><div className="text-xs text-gray-500">Disponível</div><b className="text-[#2B7A3D]">{formatCurrency(p.credito_disponivel)}</b></div>
          <div><div className="text-xs text-gray-500">Vencido</div><b className={ind.valor_vencido > 0 ? 'text-red-600' : ''}>{formatCurrency(ind.valor_vencido || 0)}</b></div>
          <div><div className="text-xs text-gray-500">Risco</div><b>{p.risco}</b></div>
        </div>
      </div>

      <Tabs defaultValue={params.get('tab') || 'resumo'}>
        <TabsList className="flex-wrap h-auto">
          {[['resumo', 'Resumo'], ['usuarios', `Usuários (${usuarios.length})`], ['imoveis', `Imóveis (${imoveis.length})`], ['ordens', `Solicitações/OS (${ordens.length})`], ['faturamento', `Faturamento (${faturas.length})`], ['financeiro', 'Financeiro'], ['documentos', 'Documentos'], ['historico', 'Histórico']].map(([v, t]) => (
            <TabsTrigger key={v} value={v} data-testid={`ptab-${v}`}>{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="resumo">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[['Solicitações', p.qtd_solicitacoes], ['OS concluídas', p.qtd_os_concluidas], ['Imóveis', p.qtd_imoveis], ['Usuários', p.qtd_usuarios],
              ['Faturamento acumulado', formatCurrency(ind.faturamento_acumulado || 0)], ['Pagamentos realizados', ind.pagamentos_realizados || 0],
              ['Pagamentos em dia', ind.pagamentos_em_dia || 0], ['Pagamentos atrasados', ind.pagamentos_atrasados || 0]].map(([l, v]) => (
              <div key={l} className="bg-white rounded-xl border border-gray-200 p-3">
                <div className="text-lg font-bold text-[#3D4F5F]">{v}</div>
                <div className="text-[11px] text-gray-500">{l}</div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 mt-3 text-sm space-y-1">
            <div><b>Razão social:</b> {p.razao_social || '-'} · <b>{p.pessoa === 'PJ' ? 'CNPJ' : 'CPF'}:</b> {p.cnpj_cpf || '-'}</div>
            <div><b>Contato:</b> {p.email || '-'} · {p.telefone || '-'}</div>
            <div><b>Endereço:</b> {p.endereco?.rua ? `${p.endereco.rua}, ${p.endereco.numero || ''} - ${p.endereco.bairro || ''}, ${p.endereco.cidade || ''}/${p.endereco.estado || ''}` : '-'}</div>
            <div><b>Parceria desde:</b> {p.data_inicio ? formatDate(p.data_inicio) : '-'} · <b>Resp. comercial:</b> {p.responsavel_comercial || '-'} · <b>Origem:</b> {p.origem || '-'}</div>
            {p.observacoes_internas && <div className="text-gray-500"><b>Obs:</b> {p.observacoes_internas}</div>}
          </div>
        </TabsContent>

        <TabsContent value="usuarios" className="space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm" data-testid="banner-acesso-portal">
            <div className="font-semibold text-[#2B7A3D] flex items-center gap-2"><KeyRound size={16} /> Acesso ao Portal do Parceiro</div>
            <div className="text-gray-600 mt-1">
              O parceiro acessa em: <a href="/parceiro/login" target="_blank" rel="noreferrer" className="font-mono font-semibold text-[#2B7A3D] underline">{window.location.origin}/parceiro/login</a>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs ml-1" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/parceiro/login`); toast.success('Link do portal copiado'); }}>Copiar link</Button>
            </div>
            <div className="text-xs text-gray-500 mt-1.5">
              1. Crie o usuário abaixo definindo <b>e-mail (login) e senha</b> → 2. Envie o link + credenciais ao parceiro → 3. Ele já entra direto (e pode trocar a senha no portal se quiser).
              Para alterar login ou senha depois, use o botão <Pencil size={11} className="inline" /> Editar na lista.
            </div>
          </div>
          <Button size="sm" className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => setShowUsuario(true)} data-testid="btn-criar-usuario"><Plus size={14} className="mr-1" /> Criar usuário</Button>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-gray-50 text-xs text-gray-500"><tr>{['Nome', 'Cargo', 'E-mail', 'Permissão', 'Último acesso', 'Status', 'Ações'].map((h) => <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100" data-testid={`usuario-${u.email}`}>
                    <td className="px-3 py-2.5 font-medium">{u.nome}</td>
                    <td className="px-3 py-2.5 text-xs">{u.cargo || '-'}</td>
                    <td className="px-3 py-2.5 text-xs">{u.email}</td>
                    <td className="px-3 py-2.5 text-xs">{u.permissao === 'admin_parceiro' ? 'Administrador' : 'Solicitante'}</td>
                    <td className="px-3 py-2.5 text-xs">{u.ultimo_acesso ? new Date(u.ultimo_acesso).toLocaleString('pt-BR') : 'Nunca acessou'}</td>
                    <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${u.ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.ativo ? 'Ativo' : 'Bloqueado'}</span></td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-xs" title="Editar login/senha e dados" onClick={() => setEditUsuario({ ...u, nova_senha: '' })} data-testid={`editar-usuario-${u.email}`}><Pencil size={13} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => toggleUsuario(u)}>{u.ativo ? 'Bloquear' : 'Reativar'}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {usuarios.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400 text-sm">Nenhum usuário. Crie o primeiro acesso ao portal.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="imoveis">
          <div className="space-y-2">
            {imoveis.map((im) => (
              <div key={im.id} className="bg-white rounded-xl border border-gray-200 p-3 text-sm">
                <b>{im.condominio ? `${im.condominio} ${im.bloco || ''} ${im.unidade || ''}` : `${im.endereco?.rua || ''}, ${im.endereco?.numero || ''}`}</b>
                {im.codigo_interno && <span className="text-xs text-gray-400 ml-1">({im.codigo_interno})</span>}
                <div className="text-xs text-gray-500">{im.endereco?.bairro} · {im.endereco?.cidade}{im.endereco?.zona ? ` · ${im.endereco.zona}` : ''} {im.morador && `· Morador: ${im.morador}`} {!im.ativo && <span className="text-red-500 font-semibold">· Desativado</span>}</div>
              </div>
            ))}
            {imoveis.length === 0 && <div className="text-sm text-gray-400 py-8 text-center">Nenhum imóvel cadastrado pelo parceiro.</div>}
          </div>
        </TabsContent>

        <TabsContent value="ordens">
          <div className="space-y-2">
            {ordens.map((o) => (
              <div key={o.id} className="bg-white rounded-xl border border-gray-200 p-3 text-sm flex items-start justify-between gap-2 flex-wrap" data-testid={`os-parceiro-${o.numero}`}>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <b>{formatOrdemNumero ? formatOrdemNumero(o.numero) : `#OS-${o.numero}`}</b>
                    <span className="text-[10px] font-bold text-white bg-[#2B7A3D] px-1.5 py-0.5 rounded">PARCEIRO</span>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-white" style={{ backgroundColor: getStatusColor(o.status) }}>{o.status}</span>
                    {o.prioridade === 'urgente' && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">URGENTE</span>}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">{o.servico_nome} · {o.cliente_nome} · {formatDate(o.data_agendamento)}</div>
                  <div className="text-[11px] text-gray-400">Solicitado por {o.parceiro_usuario_nome || '-'} · {APROVACAO_LABEL[o.aprovacao_parceiro] || '-'}{o.fatura_id ? ' · Faturada' : ''}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{o.valor > 0 ? formatCurrency(o.valor) : <span className="text-amber-600 text-xs">definir valor</span>}</div>
                  <Link to="/sistema/agendamento" className="text-[11px] text-[#2B7A3D] font-medium">Abrir no Agendamento</Link>
                </div>
              </div>
            ))}
            {ordens.length === 0 && <div className="text-sm text-gray-400 py-8 text-center">Nenhuma solicitação deste parceiro.</div>}
          </div>
        </TabsContent>

        <TabsContent value="faturamento" className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => setShowFatura(true)} data-testid="btn-gerar-fatura"><Plus size={14} className="mr-1" /> Gerar fatura</Button>
            <Button size="sm" variant="outline" onClick={() => {
              const fc = p.faturamento_config || {};
              setFatConfig({ ciclo: fc.ciclo || 'semanal', prazo_dias: fc.prazo_dias ?? 7, dia_fechamento: fc.dia_fechamento || '', desconto_tipo: fc.desconto_tipo || 'sem', desconto_pct: fc.desconto_pct || 0, condicoes: fc.condicoes || '' });
              setShowFatConfig(true);
            }} data-testid="btn-config-faturamento"><Settings2 size={14} className="mr-1" /> Configurar fechamento</Button>
            <span className="text-xs text-gray-500">
              Ciclo: <b>{CICLO_LABEL[p.faturamento_config?.ciclo] || 'Semanal'}</b>
              {p.faturamento_config?.ciclo === 'semanal' && ` · fecha ${DIAS_SEMANA.find((d) => d.v === p.faturamento_config?.dia_fechamento)?.t || 'Segunda-feira'}`}
              {p.faturamento_config?.ciclo === 'mensal' && ` · fecha dia ${p.faturamento_config?.dia_fechamento || 1}`}
              {p.faturamento_config?.ciclo === 'quinzenal' && ' · fecha dias 1 e 16'}
              {p.faturamento_config?.ciclo === 'por_servico' && ' · fatura diária por OS'}
              {' '}· Prazo: {p.faturamento_config?.prazo_dias ?? 7} dias · Desconto: {p.faturamento_config?.desconto_tipo === 'personalizado' ? `${p.faturamento_config?.desconto_pct}%` : p.faturamento_config?.desconto_tipo === 'volume' ? 'por volume' : 'sem'}
            </span>
          </div>
          <p className="text-[11px] text-gray-400">O fechamento automático roda todo dia às 06:00 e gera a fatura com as OS aprovadas ainda não faturadas quando for o dia configurado.</p>
          <div className="space-y-2">
            {faturas.map((f) => (
              <div key={f.id} className="bg-white rounded-xl border border-gray-200 p-3 text-sm flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <b>{f.codigo}</b> · {formatDate(f.periodo_inicio)} a {formatDate(f.periodo_fim)} · {f.ordens.length} OS · vence {formatDate(f.vencimento)}
                  <div className="text-xs text-gray-500">Subtotal {formatCurrency(f.subtotal)}{f.desconto > 0 && ` · desconto ${formatCurrency(f.desconto)}`}</div>
                </div>
                <div className="flex items-center gap-2">
                  <b>{formatCurrency(f.total)}</b>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${f.status === 'Paga' ? 'bg-green-100 text-green-700' : f.status === 'Vencida' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>{f.status}</span>
                  {['Aguardando Pagamento', 'Vencida', 'Aberta', 'Fechada'].includes(f.status) && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => marcarPaga(f)}>Marcar paga</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-[#2B7A3D]" title="Copiar link de pagamento PIX"
                        onClick={async () => {
                          try {
                            const r = await parceirosApi.pixFatura(f.id);
                            const link = `${window.location.origin}/pagar/${r.data.id}`;
                            await navigator.clipboard?.writeText(link);
                            toast.success(`Link PIX copiado: ${link}`);
                          } catch (err) {
                            const d = err.response?.data?.detail;
                            toast.error(typeof d === 'string' ? d : 'Erro ao gerar PIX');
                          }
                        }} data-testid={`pix-fatura-perfil-${f.codigo}`}>PIX</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {faturas.length === 0 && <div className="text-sm text-gray-400 py-6 text-center">Nenhuma fatura gerada.</div>}
          </div>
        </TabsContent>

        <TabsContent value="financeiro" className="space-y-3">
          <h3 className="text-sm font-semibold text-[#3D4F5F]">Histórico do limite de crédito</h3>
          <div className="space-y-2">
            {historico.map((h) => (
              <div key={h.id} className="bg-white rounded-xl border border-gray-200 p-3 text-sm">
                <b>{formatCurrency(h.limite_anterior)} → {formatCurrency(h.novo_limite)}</b>
                <div className="text-xs text-gray-500">{h.motivo} {h.observacao && `· ${h.observacao}`}</div>
                <div className="text-[11px] text-gray-400">Alterado por {h.alterado_por} em {h.created_at ? new Date(h.created_at).toLocaleString('pt-BR') : ''}</div>
              </div>
            ))}
            {historico.length === 0 && <div className="text-sm text-gray-400 py-6 text-center">Sem histórico.</div>}
          </div>
        </TabsContent>

        <TabsContent value="documentos" className="space-y-3">
          <label className="inline-flex items-center gap-2 text-sm font-medium text-[#2B7A3D] cursor-pointer border border-[#2B7A3D] rounded-lg px-3 py-2">
            <FileUp size={15} /> Anexar documentos
            <input type="file" multiple className="hidden" onChange={uploadDocs} data-testid="input-documentos" />
          </label>
          <div className="space-y-2">
            {documentos.map((d) => (
              <div key={d.id} className="bg-white rounded-xl border border-gray-200 p-3 text-sm flex items-center justify-between gap-2">
                <a href={d.url} target="_blank" rel="noreferrer" className="text-[#2B7A3D] font-medium truncate">{d.nome}</a>
                <div className="flex items-center gap-2 text-xs text-gray-400 shrink-0">
                  {d.uploaded_by} · {d.created_at ? formatDate(d.created_at.slice(0, 10)) : ''}
                  <button onClick={async () => { await parceirosApi.deleteDocumento(d.id); load(); }} className="text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {documentos.length === 0 && <div className="text-sm text-gray-400 py-6 text-center">Nenhum documento.</div>}
          </div>
        </TabsContent>

        <TabsContent value="historico">
          <div className="space-y-1.5">
            {auditoria.map((a) => (
              <div key={a.id} className="bg-white rounded-lg border border-gray-100 px-3 py-2 text-sm flex items-center justify-between gap-2">
                <div><b className="text-[#3D4F5F]">{a.acao}</b> {a.detalhe && <span className="text-gray-500 text-xs">· {a.detalhe}</span>}</div>
                <div className="text-[11px] text-gray-400 shrink-0">{a.usuario_nome} ({a.usuario_tipo}) · {a.created_at ? new Date(a.created_at).toLocaleString('pt-BR') : ''}</div>
              </div>
            ))}
            {auditoria.length === 0 && <div className="text-sm text-gray-400 py-6 text-center">Sem registros.</div>}
          </div>
        </TabsContent>
      </Tabs>

      <ParceiroFormDialog open={showEdit} onOpenChange={setShowEdit} initial={p} onSaved={load} />

      {/* Criar usuário */}
      <Dialog open={showUsuario} onOpenChange={setShowUsuario}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Criar usuário do parceiro</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={novoUsuario.nome} onChange={(e) => setNovoUsuario({ ...novoUsuario, nome: e.target.value })} data-testid="novo-usuario-nome" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Cargo</Label><Input value={novoUsuario.cargo} onChange={(e) => setNovoUsuario({ ...novoUsuario, cargo: e.target.value })} /></div>
              <div><Label>WhatsApp</Label><Input value={novoUsuario.whatsapp} onChange={(e) => setNovoUsuario({ ...novoUsuario, whatsapp: e.target.value })} /></div>
            </div>
            <div><Label>E-mail (login) *</Label><Input type="email" value={novoUsuario.email} onChange={(e) => setNovoUsuario({ ...novoUsuario, email: e.target.value })} data-testid="novo-usuario-email" /></div>
            <div><Label>Senha * (mín. 6 caracteres)</Label><Input value={novoUsuario.senha} onChange={(e) => setNovoUsuario({ ...novoUsuario, senha: e.target.value })} placeholder="defina a senha do parceiro" data-testid="novo-usuario-senha" /></div>
            <div>
              <Label>Permissão</Label>
              <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={novoUsuario.permissao} onChange={(e) => setNovoUsuario({ ...novoUsuario, permissao: e.target.value })}>
                <option value="solicitante">Solicitante</option>
                <option value="admin_parceiro">Administrador do parceiro</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsuario(false)}>Cancelar</Button>
            <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={busy || !novoUsuario.nome || !novoUsuario.email || novoUsuario.senha.length < 6} onClick={criarUsuario} data-testid="salvar-usuario">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar usuário (login/senha) */}
      <Dialog open={!!editUsuario} onOpenChange={(v) => !v && setEditUsuario(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-editar-usuario">
          <DialogHeader><DialogTitle>Editar usuário do parceiro</DialogTitle></DialogHeader>
          {editUsuario && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={editUsuario.nome} onChange={(e) => setEditUsuario({ ...editUsuario, nome: e.target.value })} data-testid="edit-usuario-nome" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Cargo</Label><Input value={editUsuario.cargo || ''} onChange={(e) => setEditUsuario({ ...editUsuario, cargo: e.target.value })} /></div>
                <div><Label>WhatsApp</Label><Input value={editUsuario.whatsapp || ''} onChange={(e) => setEditUsuario({ ...editUsuario, whatsapp: e.target.value })} /></div>
              </div>
              <div><Label>E-mail (login)</Label><Input type="email" value={editUsuario.email} onChange={(e) => setEditUsuario({ ...editUsuario, email: e.target.value })} data-testid="edit-usuario-email" /></div>
              <div>
                <Label>Nova senha (deixe em branco para manter a atual)</Label>
                <Input value={editUsuario.nova_senha} onChange={(e) => setEditUsuario({ ...editUsuario, nova_senha: e.target.value })} placeholder="mín. 6 caracteres" data-testid="edit-usuario-senha" />
              </div>
              <div>
                <Label>Permissão</Label>
                <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={editUsuario.permissao} onChange={(e) => setEditUsuario({ ...editUsuario, permissao: e.target.value })}>
                  <option value="solicitante">Solicitante</option>
                  <option value="admin_parceiro">Administrador do parceiro</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUsuario(null)}>Cancelar</Button>
            <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={busy || !editUsuario?.nome || !editUsuario?.email || (editUsuario?.nova_senha && editUsuario.nova_senha.length < 6)} onClick={salvarEdicaoUsuario} data-testid="salvar-edicao-usuario">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alterar limite */}
      <Dialog open={showLimite} onOpenChange={setShowLimite}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Alterar limite de crédito</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-600 space-y-0.5">
            <div>Limite atual: <b>{formatCurrency(p.limite_credito)}</b></div>
            <div>Utilizado: <b className="text-amber-600">{formatCurrency(p.credito_utilizado)}</b> · Disponível: <b className="text-[#2B7A3D]">{formatCurrency(p.credito_disponivel)}</b></div>
          </div>
          <div className="space-y-3">
            <div><Label>Novo limite (R$) *</Label><Input type="number" min="0" value={limiteForm.novo_limite} onChange={(e) => setLimiteForm({ ...limiteForm, novo_limite: e.target.value })} data-testid="input-novo-limite" /></div>
            <div><Label>Motivo *</Label><Input value={limiteForm.motivo} onChange={(e) => setLimiteForm({ ...limiteForm, motivo: e.target.value })} placeholder="ex: Bom histórico de pagamento" data-testid="input-motivo-limite" /></div>
            <div><Label>Observação</Label><Textarea rows={2} value={limiteForm.observacao} onChange={(e) => setLimiteForm({ ...limiteForm, observacao: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLimite(false)}>Cancelar</Button>
            <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={busy || limiteForm.novo_limite === '' || !limiteForm.motivo} onClick={alterarLimite} data-testid="confirmar-limite">Atualizar limite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gerar fatura */}
      <Dialog open={showFatura} onOpenChange={setShowFatura}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Gerar fatura do período</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-500">Inclui OS aprovadas pelo parceiro (crédito) e ainda não faturadas no período.</p>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Início</Label><Input type="date" value={faturaForm.periodo_inicio} onChange={(e) => setFaturaForm({ ...faturaForm, periodo_inicio: e.target.value })} data-testid="fatura-inicio" /></div>
            <div><Label>Fim</Label><Input type="date" value={faturaForm.periodo_fim} onChange={(e) => setFaturaForm({ ...faturaForm, periodo_fim: e.target.value })} data-testid="fatura-fim" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFatura(false)}>Cancelar</Button>
            <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={busy || !faturaForm.periodo_inicio || !faturaForm.periodo_fim} onClick={gerarFatura} data-testid="confirmar-gerar-fatura">Gerar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config de faturamento / fechamento automático */}
      <Dialog open={showFatConfig} onOpenChange={setShowFatConfig}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Configurar faturamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ciclo de cobrança</Label>
              <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={fatConfig.ciclo} onChange={(e) => setFatConfig({ ...fatConfig, ciclo: e.target.value, dia_fechamento: '' })} data-testid="fatconfig-ciclo">
                <option value="por_servico">Por OS (fatura individual, diária)</option>
                <option value="semanal">Semanal</option>
                <option value="quinzenal">Quinzenal (dias 1 e 16)</option>
                <option value="mensal">Mensal</option>
              </select>
            </div>
            {fatConfig.ciclo === 'semanal' && (
              <div>
                <Label>Dia de fechamento</Label>
                <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={fatConfig.dia_fechamento || 'segunda'} onChange={(e) => setFatConfig({ ...fatConfig, dia_fechamento: e.target.value })} data-testid="fatconfig-dia-semana">
                  {DIAS_SEMANA.map((d) => <option key={d.v} value={d.v}>{d.t}</option>)}
                </select>
              </div>
            )}
            {fatConfig.ciclo === 'mensal' && (
              <div>
                <Label>Dia de fechamento (1 a 28)</Label>
                <Input type="number" min="1" max="28" value={fatConfig.dia_fechamento} onChange={(e) => setFatConfig({ ...fatConfig, dia_fechamento: e.target.value })} placeholder="1" data-testid="fatconfig-dia-mes" />
              </div>
            )}
            <div>
              <Label>Prazo de pagamento</Label>
              <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={String(fatConfig.prazo_dias)} onChange={(e) => setFatConfig({ ...fatConfig, prazo_dias: e.target.value })} data-testid="fatconfig-prazo">
                <option value="0">À vista</option>
                <option value="3">3 dias</option>
                <option value="7">7 dias</option>
                <option value="15">15 dias</option>
                <option value="30">30 dias</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Desconto</Label>
                <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={fatConfig.desconto_tipo} onChange={(e) => setFatConfig({ ...fatConfig, desconto_tipo: e.target.value })} data-testid="fatconfig-desconto">
                  <option value="sem">Sem desconto</option>
                  <option value="volume">Por volume (faixas)</option>
                  <option value="personalizado">Personalizado (%)</option>
                </select>
              </div>
              {fatConfig.desconto_tipo === 'personalizado' && (
                <div><Label>%</Label><Input type="number" min="0" max="100" value={fatConfig.desconto_pct} onChange={(e) => setFatConfig({ ...fatConfig, desconto_pct: e.target.value })} /></div>
              )}
            </div>
            <div><Label>Condições especiais</Label><Textarea rows={2} value={fatConfig.condicoes} onChange={(e) => setFatConfig({ ...fatConfig, condicoes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFatConfig(false)}>Cancelar</Button>
            <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={busy} onClick={salvarFatConfig} data-testid="fatconfig-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nova solicitação (admin) */}
      <NovaSolicitacaoAdminDialog open={showSolicitacao} onOpenChange={setShowSolicitacao} parceiroId={id} imoveis={imoveis} onCreated={load} />
    </div>
  );
}

const NovaSolicitacaoAdminDialog = ({ open, onOpenChange, parceiroId, imoveis, onCreated }) => {
  const [servicos, setServicos] = useState([]);
  const [form, setForm] = useState({ imovel_id: '', cliente_nome: '', cliente_whatsapp: '', servico_id: '', descricao: '', prioridade: 'normal', data_desejada: '', periodo: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) servicosApi.list().then((r) => setServicos(r.data)).catch(() => {});
  }, [open]);

  const criar = async () => {
    setBusy(true);
    try {
      await parceirosApi.createSolicitacao(parceiroId, { ...form, novo_imovel: null, anexos: [] });
      toast.success('Solicitação criada — OS gerada');
      onOpenChange(false);
      setForm({ imovel_id: '', cliente_nome: '', cliente_whatsapp: '', servico_id: '', descricao: '', prioridade: 'normal', data_desejada: '', periodo: '' });
      onCreated && onCreated();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao criar solicitação');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nova solicitação em nome do parceiro</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Imóvel *</Label>
            <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.imovel_id} onChange={(e) => setForm({ ...form, imovel_id: e.target.value })} data-testid="admin-sol-imovel">
              <option value="">Selecione...</option>
              {imoveis.filter((i) => i.ativo !== false).map((i) => (
                <option key={i.id} value={i.id}>{i.condominio ? `${i.condominio} ${i.unidade || ''}` : `${i.endereco?.rua || ''}, ${i.endereco?.numero || ''}`} {i.codigo_interno ? `(${i.codigo_interno})` : ''}</option>
              ))}
            </select>
            {imoveis.length === 0 && <p className="text-xs text-amber-600 mt-1">O parceiro ainda não tem imóveis cadastrados.</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Cliente *</Label><Input value={form.cliente_nome} onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })} data-testid="admin-sol-cliente" /></div>
            <div><Label>WhatsApp *</Label><Input value={form.cliente_whatsapp} onChange={(e) => setForm({ ...form, cliente_whatsapp: e.target.value })} data-testid="admin-sol-whatsapp" /></div>
          </div>
          <div>
            <Label>Serviço *</Label>
            <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.servico_id} onChange={(e) => setForm({ ...form, servico_id: e.target.value })} data-testid="admin-sol-servico">
              <option value="">Selecione...</option>
              {servicos.map((s) => <option key={s.id} value={s.id}>{s.categoria_nome ? `${s.categoria_nome} · ` : ''}{s.nome}</option>)}
            </select>
          </div>
          <div><Label>Descrição</Label><Textarea rows={2} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Data *</Label><Input type="date" value={form.data_desejada} onChange={(e) => setForm({ ...form, data_desejada: e.target.value })} data-testid="admin-sol-data" /></div>
            <div>
              <Label>Período</Label>
              <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.periodo} onChange={(e) => setForm({ ...form, periodo: e.target.value })}>
                <option value="">-</option>{['Manhã', 'Tarde', 'Noite'].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label>Prioridade</Label>
              <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.prioridade} onChange={(e) => setForm({ ...form, prioridade: e.target.value })}>
                <option value="normal">Normal</option><option value="urgente">Urgente</option>
              </select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={busy || !form.imovel_id || !form.cliente_nome || !form.cliente_whatsapp || !form.servico_id || !form.data_desejada} onClick={criar} data-testid="admin-sol-criar">Criar OS</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
